import React, {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import {
  normalizeBusinessRules,
  validateBusinessRules,
} from 'src/entity/ProjectSettingsForm/ui/businessRules';
import { BusinessRulesBuilder } from 'src/entity/ProjectSettingsForm/ui/BusinessRulesBuilder';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { CreateFormRes } from 'src/shared/api/projects/types';
import { setError as setGlobalError, setSuccess as setGlobalSuccess } from 'src/shared/lib/utils';
import { FormBusinessRule } from 'src/shared/store/projects/types';
import { Button, Modal } from 'src/shared/ui';
import { recalculateCurrentFormRules } from 'src/widgets/ProjectTable/lib/recalculateCurrentFormRules';

import {
  buildConsolidationRulesFromCurrentSettings,
  buildCurrentConsolidationUpdatePlan,
  ConsolidationSourceForm,
} from '../lib/consolidation';

interface ProjectConsolidationModalProps {
  setIsOpenConsolidationModal: Dispatch<SetStateAction<boolean>>;
}

const isConsolidationRule = (rule: FormBusinessRule): boolean =>
  String(rule.type || '')
    .trim()
    .toLowerCase() === 'consolidation';

const createStarterConsolidationRule = (): FormBusinessRule => ({
  id: `consolidation_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  type: 'consolidation',
  priority: 1,
  enabled: true,
  config: {
    operation: 'sum',
  },
});

const mapSourceForm = (response: CreateFormRes): ConsolidationSourceForm => ({
  id: response.Id || response['ID'],
  alias: response.Alias,
  connection: response.FormName,
  formName: response.Alias || response.FormName,
  formDescription: response.FormDescription,
  appearanceSettings: response.AppearanceSettings,
  viewSettings: response.ViewSettings,
  formStructure: response.FormStructure as FieldType[] | undefined,
});

const getFieldDisplayName = (field: FieldType): string =>
  String(field.columnLabel || field.columnName || '')
    .trim()
    .toLowerCase();

const getSharedConsolidationOperationError = (rules: FormBusinessRule[]): string | null => {
  const operations = Array.from(
    new Set(
      rules
        .filter(isConsolidationRule)
        .map(rule =>
          String(rule.config?.operation || 'sum')
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    ),
  );

  if (operations.length > 1) {
    return 'Для всех таблиц-источников должна быть выбрана одна и та же агрегация';
  }

  return null;
};

export const ProjectConsolidationModal: FC<ProjectConsolidationModalProps> = memo(
  ({ setIsOpenConsolidationModal }) => {
    const { forms, tableSettings, setTableSettings } = useContext(ProjectsContext);
    const [rules, setRules] = useState<FormBusinessRule[]>([]);
    const [sourceForms, setSourceForms] = useState<ConsolidationSourceForm[]>([]);
    const [isLoadingSourceForms, setIsLoadingSourceForms] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const sourceFormsRequestRef = useRef<Promise<ConsolidationSourceForm[]> | null>(null);

    const currentViewConsolidationRules = useMemo(
      () => buildConsolidationRulesFromCurrentSettings(tableSettings?.viewSettings, sourceForms),
      [sourceForms, tableSettings?.viewSettings],
    );

    const legacyConsolidationRules = useMemo(
      () => (tableSettings?.businessRules || []).filter(isConsolidationRule),
      [tableSettings?.businessRules],
    );

    const availableColumns = useMemo(() => {
      const currentFormColumns =
        tableSettings?.formStructure
          ?.filter(field => field.columnType !== 'actions')
          .map(getFieldDisplayName)
          .filter(Boolean) || [];

      const sourceFormColumns = sourceForms.flatMap(sourceForm =>
        (sourceForm.formStructure || [])
          .filter(field => field.columnType !== 'actions')
          .map(getFieldDisplayName)
          .filter(Boolean),
      );

      return Array.from(new Set([...currentFormColumns, ...sourceFormColumns]));
    }, [sourceForms, tableSettings?.formStructure]);

    const availableTables = useMemo(
      () =>
        Array.from(
          new Set([
            ...forms
              .filter(form => form.id !== tableSettings?.id)
              .map(form => form.form_name?.trim())
              .filter(Boolean),
            ...sourceForms
              .map(form => form.alias?.trim() || form.formName?.trim() || form.connection?.trim())
              .filter(Boolean),
          ]),
        ),
      [forms, sourceForms, tableSettings?.id],
    );

    useEffect(() => {
      setIsDirty(false);
    }, [tableSettings?.id]);

    useEffect(() => {
      if (!tableSettings || isDirty) {
        return;
      }

      if (currentViewConsolidationRules.length) {
        setRules(currentViewConsolidationRules);
        return;
      }

      if (legacyConsolidationRules.length) {
        setRules(legacyConsolidationRules);
        return;
      }

      if (sourceForms.length) {
        setRules([createStarterConsolidationRule()]);
        return;
      }

      setRules([]);
    }, [
      currentViewConsolidationRules,
      isDirty,
      legacyConsolidationRules,
      sourceForms.length,
      tableSettings,
    ]);

    useEffect(() => {
      let isMounted = true;

      if (!tableSettings) {
        return undefined;
      }

      const otherForms = forms.filter(form => form.id !== tableSettings.id);
      if (!otherForms.length) {
        setSourceForms([]);
        sourceFormsRequestRef.current = Promise.resolve([]);
        return undefined;
      }

      const fetchSourceForms = async () => {
        setIsLoadingSourceForms(true);

        try {
          sourceFormsRequestRef.current = Promise.all(
            otherForms.map(form => ProjectsService.getTableSettings({ formId: form.id })),
          ).then(responses => responses.map(({ data }) => mapSourceForm(data)));

          const nextSourceForms = await sourceFormsRequestRef.current;
          if (!isMounted) {
            return;
          }

          setSourceForms(nextSourceForms);
        } catch (error: any) {
          if (!isMounted) {
            return;
          }

          if (error?.response?.data?.error) {
            setGlobalError(error.response.data.error);
          } else {
            setGlobalError('Не удалось загрузить таблицы-источники для консолидации');
          }
        } finally {
          if (isMounted) {
            setIsLoadingSourceForms(false);
          }
        }
      };

      fetchSourceForms();

      return () => {
        isMounted = false;
      };
    }, [forms, tableSettings]);

    const onClose = () => setIsOpenConsolidationModal(false);

    const handleRulesChange = (nextRules: FormBusinessRule[]) => {
      setIsDirty(true);
      setRules(nextRules);
    };

    const handleSave = async () => {
      if (!tableSettings) {
        return;
      }

      const parsedRules = normalizeBusinessRules(rules);
      const validationErrors = validateBusinessRules(parsedRules);
      if (validationErrors.length) {
        setGlobalError(`Проверка бизнес-правил: ${validationErrors[0]}`);
        return;
      }

      const sharedOperationError = getSharedConsolidationOperationError(parsedRules);
      if (sharedOperationError) {
        setGlobalError(sharedOperationError);
        return;
      }

      setIsSaving(true);

      try {
        const resolvedSourceForms = sourceForms.length
          ? sourceForms
          : await (sourceFormsRequestRef.current || Promise.resolve<ConsolidationSourceForm[]>([]));

        const plan = buildCurrentConsolidationUpdatePlan({
          currentFormConnection: tableSettings.connection || tableSettings.formName || '',
          currentBusinessRules: tableSettings.businessRules || [],
          currentViewSettings: tableSettings.viewSettings,
          rules: parsedRules,
          sourceForms: resolvedSourceForms,
        });

        await Promise.all(
          plan.legacyCleanupUpdates.map(sourceForm =>
            ProjectsService.updateForm({
              id: sourceForm.id,
              formName: sourceForm.formName,
              formDescription: sourceForm.formDescription,
              appearanceSettings: sourceForm.appearanceSettings || {},
              viewSettings: sourceForm.viewSettings,
            }),
          ),
        );

        await ProjectsService.updateForm({
          id: tableSettings.id,
          formName: tableSettings.formName,
          formDescription: tableSettings.formDescription,
          appearanceSettings: tableSettings.appearanceSettings || {},
          viewSettings: plan.currentViewSettings,
          businessRules: plan.currentBusinessRules,
          flkRules: tableSettings.flkRules || [],
          workflowSettings: tableSettings.workflowSettings,
          currentStatus: tableSettings.workflowCurrentStatus,
        });

        setTableSettings({
          ...tableSettings,
          viewSettings: plan.currentViewSettings,
          businessRules: plan.currentBusinessRules,
        });

        setIsDirty(false);
        await recalculateCurrentFormRules(tableSettings.id);
        setGlobalSuccess('Настройки консолидации сохранены');
        onClose();
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setGlobalError(error.response.data.error);
        } else if (error instanceof Error) {
          setGlobalError(error.message);
        } else {
          setGlobalError('Не удалось сохранить настройки консолидации');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal onClose={onClose}>
        <div className="flex w-[56rem] flex-col items-center">
          <div className="font-gothampro-500 mb-5 w-fit text-xl text-green-100">Консолидация</div>

          {isLoadingSourceForms && (
            <div className="mb-3 w-full rounded border border-blue-400 bg-blue-50 p-3 text-xs text-grey-300">
              Загружаем таблицы-источники...
            </div>
          )}

          <div className="w-full">
            <BusinessRulesBuilder
              rules={rules}
              availableColumns={availableColumns}
              availableTables={availableTables}
              onChange={handleRulesChange}
              mode="consolidation"
            />
          </div>

          <div className="mt-5 flex w-full items-center gap-3">
            <Button variant="green" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="greyLight" onClick={onClose}>
              Отменить
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);
